// src/pages/BookEditor.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Monitor } from 'lucide-react';
import {
  Save, X, Eye, User, Users, Settings, Image as ImageIcon, UserCheck, ArrowLeft, Calendar,
  Store, School, ShoppingCart, BarChart3, Package, Building, Zap, FileText, BookOpen, Plus,
  GraduationCap, TrendingUp, Palette, Target, Search, ExternalLink, BookOpenCheck, ClipboardList
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { firestoreService } from '../services/firestoreService';
import { BookSettings } from '../components/books/BookSettings';
import { TableBookEditor } from '../components/books/TableBookEditor';
import { ItemManager } from '../components/catalog/ItemManager';
import { CatalogSettingsModal } from '../components/catalog/CatalogSettings';
import { GalleryManager } from '../components/album/GalleryManager';
import { BookSettingsModal } from '../components/books/BookSettingsModal';

import { AlbumSettings } from '../components/album/AlbumSettings';

import { ProfileManager } from '../components/profile/ProfileManager';
import { ProfileSettingsModal } from '../components/profile/ProfileSettingsModal';

import { EventManager } from '../components/events/EventManager';
import { EventSettingsModal } from '../components/events/EventSettingsModal';

import { EcommerceManager } from '../components/ecommerce/EcommerceManager';
import { EcommerceSettings } from '../components/ecommerce/EcommerceSettings';

import { BookingManager } from '../components/booking/BookingManager';
import { BookingSettings } from '../components/booking/BookingSettings';

import { InstitutionManager } from '../components/institution/InstitutionManager';
import { InstitutionSettings } from '../components/institution/InstitutionSettings';

import { OrganisationManager } from '../components/organisation/OrganisationManager'
import { OrganisationSettings } from '../components/organisation/OrganisationSettings';

import { FlipbookSettings } from '../components/flipbooks/FlipbookSettings';
import { FlipbookManager } from '../components/flipbooks/FlipbookManager';

import { CourseSettings } from '../components/course/CourseSettings';
import { CourseManager } from '../components/course/manager/CourseManager';

import { LeitnerSettings } from '../components/leitner/LeitnerSettings';
import { LeitnerManager } from '../components/leitner/LeitnerManager';

import { WebSearchSettings } from '../components/websearch/WebSearchSettings';
import { WebSearchManager } from '../components/websearch/WebSearchManager';

import RequestsManager from '../components/requests/RequestsManager';
import RequestsSettings from '../components/requests/RequestsSettings';
import ProviderDashboard from '../components/requests/ProviderDashboard';

import { DiarySettings } from '../components/diary/DiarySettings';
import { DiaryManager } from '../components/diary/DiaryManager';

import { SchoolmateSettings } from '../components/schoolmate/SchoolmateSettings';
import { SchoolmateManager } from '../components/schoolmate/SchoolmateManager';

import SlidesSettings from '../components/slides/SlidesSettings';
import SlidesManager from '../components/slides/SlidesManager';



export const BookEditor = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [book, setBook] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [hasChanges, setHasChanges] = useState(false);

  const [showCatalogSettings, setShowCatalogSettings] = useState(false);
  const [showItemManager, setShowItemManager] = useState(false);

  const [showGalleryManager, setShowGalleryManager] = useState(false);
  const [showAlbumSettings, setShowAlbumSettings] = useState(false);

  const [showEventManager, setShowEventManager] = useState(false);
  const [showEventSettings, setShowEventSettings] = useState(false);

  const [showProfileManager, setShowProfileManager] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showBookSettingsModal, setShowBookSettingsModal] = useState(false);

  const [showEcommerceSettings, setShowEcommerceSettings] = useState(false);
  const [showEcommerceManager, setShowEcommerceManager] = useState(false);

  const [showBookingManager, setShowBookingManager] = useState(false);
  const [showBookingSettings, setShowBookingSettings] = useState(false);

  const [showInstitutionSettings, setShowInstitutionSettings] = useState(false);
  const [showInstitutionManager, setShowInstitutionManager] = useState(false);

  const [showOrganisationManager, setShowOrganisationManager] = useState(false);
  const [showOrganisationSettings, setShowOrganisationSettings] = useState(false);

  const [showFlipbookSettings, setShowFlipbookSettings] = useState(false);
  const [showFlipbookManager, setShowFlipbookManager] = useState(false);

  const [showCourseSettings, setShowCourseSettings] = useState(false);
  const [showCourseManager, setShowCourseManager] = useState(false);

  const [showLeitnerSettings, setShowLeitnerSettings] = useState(false);
  const [showLeitnerManager, setShowLeitnerManager] = useState(false);

  const [showWebSearchSettings, setShowWebSearchSettings] = useState(false);
  const [showWebSearchManager, setShowWebSearchManager] = useState(false);

  const [showRequestsSettings, setShowRequestsSettings] = useState(false);
  const [showRequestsManager, setShowRequestsManager] = useState(false);
  const [showProviderDashboard, setShowProviderDashboard] = useState(false);

  const [showDiarySettings, setShowDiarySettings] = useState(false);
  const [showDiaryManager, setShowDiaryManager] = useState(false);

  const [showSchoolmateSettings, setShowSchoolmateSettings] = useState(false);
  const [showSchoolmateManager, setShowSchoolmateManager] = useState(false);

  const [showSlidesSettings, setShowSlidesSettings] = useState(false);
  const [showSlidesManager, setShowSlidesManager] = useState(false);


  useEffect(() => {
    if (bookId) {
      loadBookData();
    }
  }, [bookId]);

  const loadBookData = async () => {
    try {
      const [bookData, messagesData] = await Promise.all([
        firestoreService.getBook(bookId),
        firestoreService.getBookMessages(bookId)
      ]);

      if (!bookData) {
        navigate('/dashboard');
        return;
      }

      // Check if user owns this book
      if (bookData.ownerId !== user?.uid) {
        navigate('/dashboard');
        return;
      }

      setBook(bookData);
      setMessages(messagesData);
    } catch (error) {
      console.error('Error loading book:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('WebSearch Settings Modal State:', showRequestsSettings);
  }, [showRequestsSettings]);

  const handleSave = async () => {
    if (!hasChanges) return;

    setSaving(true);
    try {
      // Update book metadata
      await firestoreService.updateBook(bookId, {
        ...book,
        updatedAt: new Date()
      });

      // Update messages
      await firestoreService.updateBookMessages(bookId, messages);

      setHasChanges(false);
      alert('Book saved successfully!');
    } catch (error) {
      console.error('Error saving book:', error);
      alert('Error saving book');
    } finally {
      setSaving(false);
    }
  };

  const handleMessagesChange = (updatedMessages) => {
    setMessages(updatedMessages);
    setHasChanges(true);
  };

  const handleBookUpdate = (updatedBook) => {
    setBook(updatedBook);
    setHasChanges(true);
  };

  const addMessage = () => {
    const newMessage = {
      messageId: (messages.length + 1).toString(),
      botId: book.botId,
      botName: book.botName,
      botCategory: book.botCategory,
      type: 'text',
      condition: '',
      section: 'New Section',
      message: 'New message...',
      userResponseType: '',
      variable: '',
      action: '',
      mediaUrl: '',
      integrationType: '',
      integrationParameter: '',
      accessLevel: '',
      authUsers: '',
      order: messages.length,
      bookId,
      status: 'active',
      createdAt: new Date()
    };

    handleMessagesChange([...messages, newMessage]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Book not found</h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-full mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>

              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {book?.botName}
                </h1>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="capitalize">{book?.botCategory}</span>
                  {book?.templateType === 'catalog' && (
                    <>
                      <span>•</span>
                      <span>{book?.itemCount || 0} items</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Catalog-specific buttons */}
              {book?.templateType === 'catalog' ? (
                <>
                  <button
                    onClick={() => setShowCatalogSettings(true)}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Catalog Settings
                  </button>

                  <button
                    onClick={() => setShowItemManager(true)}
                    className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 flex items-center gap-2"
                  >
                    <Package className="h-4 w-4" />
                    Manage Items
                  </button>
                </>

              ) : book?.templateType === 'diary' ? (
                <>
                  <button
                    onClick={() => setShowDiarySettings(true)}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Diary Settings
                  </button>

                  <button
                    onClick={() => setShowDiaryManager(true)}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <Calendar className="h-4 w-4" />
                    Manage Diary
                  </button>
                </>
              ) : book?.templateType === 'album' ? (
                // Album template buttons
                <>
                  <button
                    onClick={() => setShowAlbumSettings(true)}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Album Settings
                  </button>

                  <button
                    onClick={() => setShowGalleryManager(true)}
                    className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 flex items-center gap-2"
                  >
                    <ImageIcon className="h-4 w-4" />
                    Manage Galleries
                  </button>
                </>
              ) : book?.templateType === 'organisation' ? (
                <>
                  <button
                    onClick={() => setShowOrganisationSettings(true)}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Organisation Settings
                  </button>

                  <button
                    onClick={() => setShowOrganisationManager(true)}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <Building className="h-4 w-4" />
                    Manage Organisation
                  </button>
                </>
              ) : book?.templateType === 'ecommerce' ? (
                // eCommerce template buttons
                <>
                  <button
                    onClick={() => setShowEcommerceSettings(true)}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Store Settings
                  </button>

                  <button
                    onClick={() => setShowEcommerceManager(true)}
                    className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    Manage Store
                  </button>
                </>
              ) : (book?.templateType === 'profile') ? (
                <>
                  <button
                    onClick={() => setShowProfileSettings(true)}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Profile Settings
                  </button>

                  <button
                    onClick={() => setShowProfileManager(true)}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
                  >
                    <UserCheck className="h-4 w-4" />
                    Manage Sections
                  </button>
                </>

              ) : book?.templateType === 'events' ? (
                <>
                  <button
                    onClick={() => setShowEventSettings(true)}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Event Settings
                  </button>

                  <button
                    onClick={() => setShowEventManager(true)}
                    className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
                  >
                    <Calendar className="h-4 w-4" />
                    Manage Event
                  </button>
                </>
              ) : book?.templateType === 'course' ? (
                <>
                  <button
                    onClick={() => setShowCourseSettings(true)}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Course Settings
                  </button>

                  <button
                    onClick={() => setShowCourseManager(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <GraduationCap className="h-4 w-4" />
                    Manage Courses
                  </button>
                </>

              ) : book?.templateType === 'websearch' ? (
                <>
                  <button
                    onClick={() => setShowWebSearchSettings(true)}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    WebSearch Settings
                  </button>

                  <button
                    onClick={() => setShowWebSearchManager(true)}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <Search className="h-4 w-4" />
                    Manage Searches
                  </button>
                </>
              ) : book?.templateType === 'schoolmate' ? (
                <>
                  <button
                    onClick={() => setShowSchoolmateSettings(true)}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    School Settings
                  </button>

                  <button
                    onClick={() => setShowSchoolmateManager(true)}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <GraduationCap className="h-4 w-4" />
                    Manage School
                  </button>
                </>
              ) : book?.templateType === 'institution' ? (
                <>
                  <button
                    onClick={() => setShowInstitutionSettings(true)}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Institution Settings
                  </button>

                  <button
                    onClick={() => {/* Add institution management */ }}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <School className="h-4 w-4" />
                    Manage Institution
                  </button>
                </>
              ) : book?.templateType === 'slides' ? (
                <>
                  <button
                    onClick={() => setShowSlidesSettings(true)}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Slides Settings
                  </button>

                  <button
                    onClick={() => setShowSlidesManager(true)}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <Monitor className="h-4 w-4" />
                    Manage Slides
                  </button>
                </>
              ) : (
                // Regular template buttons
                <>
                  <button
                    onClick={() => setActiveTab('settings')}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 ${activeTab === 'settings'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                  </button>
                </>
              )}

              <button
                onClick={() => navigate(`/books/${bookId}`)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Eye className="h-4 w-4" />
                Preview
              </button>

              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {book?.templateType === 'catalog' ? (
          // Catalog editor interface
          <div className="container mx-auto px-6 py-4">
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="text-center py-12">
                <Store className="mx-auto h-16 w-16 text-orange-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Catalog Management
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Use the tools above to customize your catalog settings and manage your product items.
                  Your catalog will automatically organize and display your products in a beautiful grid layout.
                </p>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowCatalogSettings(true)}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Configure Catalog
                  </button>
                  <button
                    onClick={() => setShowItemManager(true)}
                    className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 flex items-center gap-2"
                  >
                    <Package className="h-4 w-4" />
                    Add Products
                  </button>
                </div>
              </div>

              {/* Catalog Stats */}
              <div className="border-t pt-4 mt-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{book.itemCount || 0}</p>
                    <p className="text-sm text-blue-700">Total Items</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{book.analytics?.views || 0}</p>
                    <p className="text-sm text-green-700">Total Views</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{book.gridColumns || 3}</p>
                    <p className="text-sm text-purple-700">Grid Columns</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">
                      {book.published ? 'Published' : 'Draft'}
                    </p>
                    <p className="text-sm text-orange-700">Status</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : book?.templateType === 'album' ? (
          <div className="container mx-auto px-6 py-8">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="text-center py-12">
                <ImageIcon className="mx-auto h-16 w-16 text-pink-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Album Management
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Create beautiful photo and video galleries with masonry layouts, social interactions, and sharing features. Organize your media into multiple galleries.
                </p>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowAlbumSettings(true)}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Configure Album
                  </button>
                  <button
                    onClick={() => setShowGalleryManager(true)}
                    className="bg-pink-600 text-white px-6 py-3 rounded-lg hover:bg-pink-700 flex items-center gap-2"
                  >
                    <ImageIcon className="h-4 w-4" />
                    Add Media
                  </button>
                </div>
              </div>

              {/* Album Stats */}
              <div className="border-t pt-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-pink-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-pink-600">{book.galleryCount || 0}</p>
                    <p className="text-sm text-pink-700">Galleries</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{book.mediaCount || 0}</p>
                    <p className="text-sm text-purple-700">Media Items</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{book.analytics?.views || 0}</p>
                    <p className="text-sm text-blue-700">Total Views</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {book.published ? 'Published' : 'Draft'}
                    </p>
                    <p className="text-sm text-green-700">Status</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        ) : book?.templateType === 'events' ? (
          // Events editor interface
          <div className="container mx-auto px-6 py-8">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="text-center py-12">
                <Calendar className="mx-auto h-16 w-16 text-emerald-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Event Management
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Manage your event bookings, track milestones, coordinate suppliers, and organize galleries.
                  Configure booking settings and enable features like gift registries and check-in systems.
                </p>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowEventSettings(true)}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Configure Event
                  </button>
                  <button
                    onClick={() => setShowEventManager(true)}
                    className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
                  >
                    <Calendar className="h-4 w-4" />
                    Manage Event
                  </button>
                </div>
              </div>

              {/* Event Stats */}
              <div className="border-t pt-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">{book.bookingCount || 0}</p>
                    <p className="text-sm text-emerald-700">Total Bookings</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{book.milestoneCount || 0}</p>
                    <p className="text-sm text-blue-700">Milestones</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{book.analytics?.views || 0}</p>
                    <p className="text-sm text-purple-700">Total Views</p>
                  </div>
                  <div className="bg-pink-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-pink-600">
                      {book.published ? 'Published' : 'Draft'}
                    </p>
                    <p className="text-sm text-pink-700">Status</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : book?.templateType === 'ecommerce' ? (
          // eCommerce editor interface
          <div className="container mx-auto px-6 py-8">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="text-center py-12">
                <ShoppingCart className="mx-auto h-16 w-16 text-emerald-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  eCommerce Store Management
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Use the tools above to customize your store settings and manage your products, orders, and customers.
                  Your online store will handle everything from product display to payment processing.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                  <div className="bg-emerald-50 p-6 rounded-lg">
                    <Package className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
                    <h4 className="font-semibold text-emerald-900">Product Management</h4>
                    <p className="text-sm text-emerald-700 mt-2">Add products, manage inventory, set pricing</p>
                  </div>
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <ShoppingCart className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                    <h4 className="font-semibold text-blue-900">Order Processing</h4>
                    <p className="text-sm text-blue-700 mt-2">Handle orders, track fulfillment, manage customers</p>
                  </div>
                  <div className="bg-purple-50 p-6 rounded-lg">
                    <BarChart3 className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                    <h4 className="font-semibold text-purple-900">Analytics</h4>
                    <p className="text-sm text-purple-700 mt-2">Track sales, monitor performance, insights</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        ) : book?.templateType === 'flipbook' ? (
          <>
            <button
              onClick={() => setShowFlipbookSettings(true)}
              className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              Flipbook Settings
            </button>

            <button
              onClick={() => setShowFlipbookManager(true)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            >
              <BookOpen className="h-4 w-4" />
              Manage Flipbook
            </button>
          </>
        ) : book?.templateType === 'booking' ? (
          <>
            <button
              onClick={() => setShowBookingSettings(true)}
              className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              Booking Settings
            </button>

            <button
              onClick={() => setShowBookingManager(true)}
              className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
            >
              <Calendar className="h-4 w-4" />
              Manage Bookings
            </button>
          </>

        ) : book?.templateType === 'organisation' ? (
          <div className="container mx-auto px-6 py-8">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

                {/* Organisation Content */}
                <div className="p-8">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      Organisation Management Center
                    </h2>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                      Configure your organisation's website, manage content, and enable powerful management features.
                    </p>

                    <div className="flex justify-center gap-4 mt-6">
                      <button
                        onClick={() => setShowOrganisationSettings(true)}
                        className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                      >
                        <Settings className="h-4 w-4" />
                        Organisation Settings
                      </button>
                      <button
                        onClick={() => setShowOrganisationManager(true)}
                        className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                      >
                        <Building className="h-4 w-4" />
                        Manage Content
                      </button>
                    </div>
                  </div>

                  {/* Organisation Stats */}
                  <div className="border-t pt-6 mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-indigo-50 p-4 rounded-lg">
                        <p className="text-2xl font-bold text-indigo-600">
                          {Object.keys(book?.managementFeatures || {}).filter(key => book?.managementFeatures[key]?.enabled).length}
                        </p>
                        <p className="text-sm text-indigo-700">Management Features</p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">{book?.analytics?.views || 0}</p>
                        <p className="text-sm text-blue-700">Website Views</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-2xl font-bold text-green-600 capitalize">
                          {book?.organisationType || 'business'}
                        </p>
                        <p className="text-sm text-green-700">Organisation Type</p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-lg">
                        <p className="text-2xl font-bold text-orange-600">
                          {book?.interactions ? Object.keys(book.interactions).filter(key => book.interactions[key]?.enabled).length : 0}
                        </p>
                        <p className="text-sm text-orange-700">Interactive Features</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setShowOrganisationManager(true)}>
                        <div className="flex items-center gap-3 mb-2">
                          <Users className="h-5 w-5 text-blue-600" />
                          <span className="font-medium">Manage Team</span>
                        </div>
                        <p className="text-sm text-gray-600">Add team members and leadership information</p>
                      </div>

                      <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setShowOrganisationManager(true)}>
                        <div className="flex items-center gap-3 mb-2">
                          <Calendar className="h-5 w-5 text-green-600" />
                          <span className="font-medium">Manage Events</span>
                        </div>
                        <p className="text-sm text-gray-600">Create and manage upcoming events</p>
                      </div>

                      <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setShowOrganisationSettings(true)}>
                        <div className="flex items-center gap-3 mb-2">
                          <Zap className="h-5 w-5 text-purple-600" />
                          <span className="font-medium">Enable Features</span>
                        </div>
                        <p className="text-sm text-gray-600">Configure management and interactive features</p>
                      </div>

                      <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                        onClick={() => window.open(`/books/${bookId}`, '_blank')}>
                        <div className="flex items-center gap-3 mb-2">
                          <Eye className="h-5 w-5 text-indigo-600" />
                          <span className="font-medium">Preview Website</span>
                        </div>
                        <p className="text-sm text-gray-600">See how your organisation website looks</p>
                      </div>

                      <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setShowOrganisationManager(true)}>
                        <div className="flex items-center gap-3 mb-2">
                          <FileText className="h-5 w-5 text-orange-600" />
                          <span className="font-medium">Update Content</span>
                        </div>
                        <p className="text-sm text-gray-600">Edit about, services, and contact information</p>
                      </div>

                      <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setShowOrganisationSettings(true)}>
                        <div className="flex items-center gap-3 mb-2">
                          <Settings className="h-5 w-5 text-gray-600" />
                          <span className="font-medium">Website Settings</span>
                        </div>
                        <p className="text-sm text-gray-600">Configure design, SEO, and advanced options</p>
                      </div>
                    </div>
                  </div>

                  {/* Management Features Preview */}
                  {book?.managementFeatures && Object.keys(book.managementFeatures).some(key => book.managementFeatures[key]?.enabled) && (
                    <div className="border-t pt-6 mt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Enabled Management Features</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {Object.entries(book.managementFeatures).filter(([key, config]) => config?.enabled).map(([feature, config]) => (
                          <div key={feature} className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                            <div className="font-medium text-purple-900 capitalize">{feature.replace(/([A-Z])/g, ' $1')}</div>
                            <div className="text-xs text-purple-700">Active</div>
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-gray-600 mt-3">
                        These management features are enabled and available through the content manager.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : book?.templateType === 'slides' ? (
          <div className="container mx-auto px-6 py-8">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="text-center py-12">
                <Monitor className="mx-auto h-16 w-16 text-indigo-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Slides & Presentation Management
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Upload your slides, set up interactive elements like polls and MCQs, and manage your live presentation sessions.
                </p>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowSlidesSettings(true)}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Configure Presentation
                  </button>
                  <button
                    onClick={() => setShowSlidesManager(true)}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <Monitor className="h-4 w-4" />
                    Manage Slides
                  </button>
                </div>
              </div>

              {/* Slides Stats */}
              <div className="border-t pt-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-indigo-600">{book.slides?.length || 0}</p>
                    <p className="text-sm text-indigo-700">Total Slides</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{book.analytics?.interactions || 0}</p>
                    <p className="text-sm text-purple-700">Total Interactions</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{book.analytics?.views || 0}</p>
                    <p className="text-sm text-blue-700">Live Viewers</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {book.published ? 'Published' : 'Draft'}
                    </p>
                    <p className="text-sm text-green-700">Status</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : book?.templateType === 'profile' ? (
          <div className="container mx-auto px-6 py-8">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="text-center py-12">
                <User className="mx-auto h-16 w-16 text-purple-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Profile Management
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Build your professional profile with customizable sections.
                  Add hero sections, experience, projects, achievements, and contact information.
                </p>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowProfileSettings(true)}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Profile Settings
                  </button>
                  <button
                    onClick={() => setShowProfileManager(true)}
                    className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 flex items-center gap-2"
                  >
                    <UserCheck className="h-4 w-4" />
                    Manage Sections
                  </button>
                </div>
              </div>

              {/* Profile Stats */}
              <div className="border-t pt-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{book.sectionCount || 0}</p>
                    <p className="text-sm text-purple-700">Profile Sections</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{book.analytics?.views || 0}</p>
                    <p className="text-sm text-blue-700">Profile Views</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{book.subCategory || 'personal'}</p>
                    <p className="text-sm text-green-700">Profile Type</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">
                      {book.published ? 'Published' : 'Draft'}
                    </p>
                    <p className="text-sm text-orange-700">Status</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        ) : book?.templateType === 'course' ? (
          // Course Learning Platform UI
          <div className="container mx-auto px-6 py-8">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="text-center py-12">
                <GraduationCap className="mx-auto h-16 w-16 text-blue-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Learning Platform Management
                </h3>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                  Create and manage courses with interactive lessons, quizzes, and assessments.
                  Track student progress, award certificates, and build a complete learning experience.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 mb-8">
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <BookOpen className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                    <h4 className="font-semibold text-blue-900">Course Creation</h4>
                    <p className="text-sm text-blue-700 mt-2">Build courses with video, text, code, and interactive content</p>
                  </div>
                  <div className="bg-purple-50 p-6 rounded-lg">
                    <Users className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                    <h4 className="font-semibold text-purple-900">Student Management</h4>
                    <p className="text-sm text-purple-700 mt-2">Track enrollments, progress, and achievements</p>
                  </div>
                  <div className="bg-green-50 p-6 rounded-lg">
                    <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-3" />
                    <h4 className="font-semibold text-green-900">Analytics & Reports</h4>
                    <p className="text-sm text-green-700 mt-2">Monitor course performance and student engagement</p>
                  </div>
                </div>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowCourseSettings(true)}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Platform Settings
                  </button>
                  <button
                    onClick={() => setShowCourseManager(true)}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <GraduationCap className="h-4 w-4" />
                    Manage Courses
                  </button>
                </div>
              </div>

              {/* Course Platform Stats */}
              <div className="border-t pt-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{book.courseCount || 0}</p>
                    <p className="text-sm text-blue-700">Total Courses</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{book.studentCount || 0}</p>
                    <p className="text-sm text-green-700">Active Students</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{book.instructorCount || 1}</p>
                    <p className="text-sm text-purple-700">Instructors</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">
                      {book.courseSettings?.enableGamification ? 'Enabled' : 'Disabled'}
                    </p>
                    <p className="text-sm text-yellow-700">Gamification</p>
                  </div>
                </div>
              </div>

              {/* Quick Course List Preview */}
              {book.courseCount > 0 && (
                <div className="border-t pt-6 mt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Recent Courses</h4>
                  <div className="space-y-3">
                    {/* This will be populated dynamically */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Click "Manage Courses" to view and edit your courses</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

        ) : book?.templateType === 'diary' ? (
          <div className="container mx-auto px-6 py-8">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="text-center py-12">
                <Calendar className="mx-auto h-16 w-16 text-indigo-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Diary & Planner Management
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Manage meetings, calendar events, journal entries, and contacts.
                  Configure AI-powered suggestions for agendas and meeting notes.
                </p>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowDiarySettings(true)}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Configure Diary
                  </button>
                  <button
                    onClick={() => setShowDiaryManager(true)}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <Calendar className="h-4 w-4" />
                    Manage Content
                  </button>
                </div>
              </div>

              {/* Diary Feature Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
                <div className="bg-indigo-50 p-4 rounded-lg text-center">
                  <Calendar className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-indigo-900">Meetings</h4>
                  <p className="text-sm text-indigo-700 mt-1">Schedule & manage</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <BookOpenCheck className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-green-900">Journal</h4>
                  <p className="text-sm text-green-700 mt-1">Personal entries</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <Users className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-purple-900">Contacts</h4>
                  <p className="text-sm text-purple-700 mt-1">Address book</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg text-center">
                  <Zap className="h-8 w-8 text-amber-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-amber-900">AI Assist</h4>
                  <p className="text-sm text-amber-700 mt-1">Smart suggestions</p>
                </div>
              </div>

              {/* Diary Stats */}
              <div className="border-t pt-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-indigo-600">
                      {book?.diarySettings?.enabledFeatures?.length || 0}
                    </p>
                    <p className="text-sm text-indigo-700">Enabled Features</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{book?.analytics?.views || 0}</p>
                    <p className="text-sm text-blue-700">Total Views</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-green-600 capitalize">
                      {book?.diarySettings?.theme || 'light'}
                    </p>
                    <p className="text-sm text-green-700">Theme</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">
                      {book?.published ? 'Published' : 'Draft'}
                    </p>
                    <p className="text-sm text-purple-700">Status</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setShowDiaryManager(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Calendar className="h-5 w-5 text-indigo-600" />
                      <span className="font-medium">Create Meeting</span>
                    </div>
                    <p className="text-sm text-gray-600">Schedule a new meeting with agenda</p>
                  </div>

                  <div
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setShowDiaryManager(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <BookOpenCheck className="h-5 w-5 text-green-600" />
                      <span className="font-medium">Write Entry</span>
                    </div>
                    <p className="text-sm text-gray-600">Add a new journal entry</p>
                  </div>

                  <div
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setShowDiarySettings(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Zap className="h-5 w-5 text-amber-600" />
                      <span className="font-medium">Configure AI</span>
                    </div>
                    <p className="text-sm text-gray-600">Set up AI context and suggestions</p>
                  </div>

                  <div
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => window.open(`/books/${bookId}`, '_blank')}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Eye className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">Preview Diary</span>
                    </div>
                    <p className="text-sm text-gray-600">See how your diary looks</p>
                  </div>

                  <div
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setShowDiaryManager(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Users className="h-5 w-5 text-purple-600" />
                      <span className="font-medium">Manage Contacts</span>
                    </div>
                    <p className="text-sm text-gray-600">Add and organize contacts</p>
                  </div>

                  <div
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setShowDiarySettings(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Settings className="h-5 w-5 text-gray-600" />
                      <span className="font-medium">Settings</span>
                    </div>
                    <p className="text-sm text-gray-600">Configure features and appearance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        ) : book?.templateType === 'requests' ? (
          <div className="container mx-auto px-6 py-4">
            <div className="bg-white rounded-xl shadow-sm p-4">

              {/* HEADER */}
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg shadow-lg">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Requests Template
                  </h3>
                  <p className="text-gray-600">
                    AI-assisted service request system for users to post requests and match with providers.
                  </p>
                </div>
              </div>

              {/* QUICK STATS */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-emerald-100">
                  <div className="flex items-center gap-2 text-emerald-600 mb-1">
                    <Users className="h-4 w-4" />
                    <span className="text-xs font-medium uppercase tracking-wide">Total Requests</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {book?.stats?.totalRequests || 0}
                  </p>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-teal-100">
                  <div className="flex items-center gap-2 text-teal-600 mb-1">
                    <UserCheck className="h-4 w-4" />
                    <span className="text-xs font-medium uppercase tracking-wide">Completed</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {book?.stats?.completedRequests || 0}
                  </p>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-blue-100">
                  <div className="flex items-center gap-2 text-blue-600 mb-1">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-xs font-medium uppercase tracking-wide">Providers</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {book?.stats?.providerCount || 0}
                  </p>
                </div>
              </div>

              {/* ACTION BUTTONS */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRequestsSettings(true)}
                  className="flex-1 bg-white border-2 border-emerald-200 text-emerald-700 px-6 py-3 rounded-lg
                            hover:bg-emerald-50 hover:border-emerald-300 transition-all
                            flex items-center justify-center gap-2 font-medium shadow-sm"
                >
                  <Settings className="h-5 w-5" />
                  Requests Settings
                </button>

                <button
                  onClick={() => setShowRequestsManager(true)}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-3 rounded-lg
                            hover:from-emerald-700 hover:to-teal-700 transition-all
                            flex items-center justify-center gap-2 font-medium shadow-lg shadow-emerald-200"
                >
                  <Users className="h-5 w-5" />
                  Manage Requests
                </button>

                <button
                  onClick={() => setShowProviderDashboard(true)}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg
                            hover:from-blue-700 hover:to-indigo-700 transition-all
                            flex items-center justify-center gap-2 font-medium shadow-lg shadow-blue-200"
                >
                  <User className="h-5 w-5" />
                  Provider Profiles
                </button>
              </div>

              {/* FEATURE HIGHLIGHTS */}
              <div className="mt-6 pt-6 border-t border-emerald-100">
                <p className="text-sm font-medium text-gray-700 mb-3">Key Features:</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-500"></div>
                    <span>AI-Assisted Matching</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="h-1.5 w-1.5 rounded-full bg-teal-500"></div>
                    <span>Provider Directory</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="h-1.5 w-1.5 rounded-full bg-blue-500"></div>
                    <span>Real-Time Offers</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="h-1.5 w-1.5 rounded-full bg-indigo-500"></div>
                    <span>Analytics</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="h-1.5 w-1.5 rounded-full bg-green-500"></div>
                    <span>Automated Routing</span>
                  </div>

                </div>
              </div>

              {/* QUICK ACTIONS */}
              <div className="mt-6 pt-6 border-t border-emerald-100">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowRequestsSettings(true)}
                    className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg
                              hover:bg-gray-50 flex items-center gap-2 text-sm transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure Requests
                  </button>

                  <button
                    onClick={() => window.open(`/books/${book.id}`, '_blank')}
                    className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg
                              hover:bg-gray-50 flex items-center gap-2 text-sm transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Preview
                  </button>
                </div>
              </div>

            </div>
          </div>

        ) : book?.templateType === 'websearch' ? (
          <div className="container mx-auto px-6 py-4">
            <div className="bg-white rounded-xl shadow-sm p-4">
              {/* WebSearch Template Preview */}
              <div className="border-t pt-4 mt-2">
                <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 rounded-xl p-8 border border-indigo-100">
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg shadow-lg">
                      <Search className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">WebSearch Template</h3>
                      <p className="text-gray-600">
                        AI-powered lead generation and market research tool. Find leads, suppliers, and tenders with intelligent search and engagement tracking.
                      </p>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-indigo-100">
                      <div className="flex items-center gap-2 text-indigo-600 mb-1">
                        <Users className="h-4 w-4" />
                        <span className="text-xs font-medium uppercase tracking-wide">Total Items</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        {book?.stats?.totalItems || 0}
                      </p>
                    </div>

                    <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-purple-100">
                      <div className="flex items-center gap-2 text-purple-600 mb-1">
                        <Target className="h-4 w-4" />
                        <span className="text-xs font-medium uppercase tracking-wide">Saved Items</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        {book?.stats?.savedItems || 0}
                      </p>
                    </div>

                    <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-blue-100">
                      <div className="flex items-center gap-2 text-blue-600 mb-1">
                        <TrendingUp className="h-4 w-4" />
                        <span className="text-xs font-medium uppercase tracking-wide">Searches</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        {book?.stats?.totalSearches || 0}
                      </p>
                    </div>
                  </div>

                  {/* Enabled Categories */}
                  {book?.websearchSettings?.enabledCategories && (
                    <div className="mb-6">
                      <p className="text-sm font-medium text-gray-700 mb-2">Enabled Categories:</p>
                      <div className="flex flex-wrap gap-2">
                        {book.websearchSettings.enabledCategories.map(category => (
                          <span
                            key={category}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 rounded-lg text-sm font-medium"
                          >
                            {category === 'lead' && <Users className="h-3.5 w-3.5" />}
                            {category === 'supplier' && <Package className="h-3.5 w-3.5" />}
                            {category === 'tender' && <FileText className="h-3.5 w-3.5" />}
                            <span className="capitalize">{category}s</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Grounding URLs */}
                  {book?.websearchSettings?.groundingUrls?.length > 0 && (
                    <div className="mb-6 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-indigo-100">
                      <p className="text-sm font-medium text-gray-700 mb-2">Search Focus:</p>
                      <div className="flex flex-wrap gap-2">
                        {book.websearchSettings.groundingUrls.slice(0, 3).map((url, index) => (
                          <span
                            key={index}
                            className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded"
                          >
                            {url}
                          </span>
                        ))}
                        {book.websearchSettings.groundingUrls.length > 3 && (
                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                            +{book.websearchSettings.groundingUrls.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        console.log('Opening WebSearch Settings'); // Debug log
                        setShowWebSearchSettings(true);
                      }}
                      className="flex-1 bg-white border-2 border-indigo-200 text-indigo-700 px-6 py-3 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 transition-all flex items-center justify-center gap-2 font-medium shadow-sm"
                    >
                      <Settings className="h-5 w-5" />
                      WebSearch Settings
                    </button>

                    <button
                      onClick={() => {
                        console.log('Opening WebSearch Manager'); // Debug log
                        setShowWebSearchManager(true);
                      }}
                      className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2 font-medium shadow-lg shadow-indigo-200"
                    >
                      <Search className="h-5 w-5" />
                      Manage Searches
                    </button>
                  </div>

                  {/* Feature Highlights */}
                  <div className="mt-6 pt-6 border-t border-indigo-100">
                    <p className="text-sm font-medium text-gray-700 mb-3">Key Features:</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="h-1.5 w-1.5 rounded-full bg-indigo-500"></div>
                        <span>AI-Powered Search</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="h-1.5 w-1.5 rounded-full bg-purple-500"></div>
                        <span>Lead Generation</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="h-1.5 w-1.5 rounded-full bg-blue-500"></div>
                        <span>Engagement Tracking</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="h-1.5 w-1.5 rounded-full bg-indigo-500"></div>
                        <span>CSV Export</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="h-1.5 w-1.5 rounded-full bg-purple-500"></div>
                        <span>Bulk Import</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="h-1.5 w-1.5 rounded-full bg-blue-500"></div>
                        <span>Custom Domains</span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="mt-6 pt-6 border-t border-indigo-100">
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setShowWebSearchSettings(true)}
                        className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm transition-colors"
                      >
                        <Target className="h-4 w-4" />
                        Configure Search
                      </button>
                      <button
                        onClick={() => window.open(`/books/${book.id}`, '_blank')}
                        className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm transition-colors"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Preview
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Configuration Options */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setShowWebSearchSettings(true)}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Settings className="h-5 w-5 text-indigo-600" />
                    <span className="font-medium">General Settings</span>
                  </div>
                  <p className="text-sm text-gray-600">Configure categories, results, and display options</p>
                </div>

                <div
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setShowWebSearchSettings(true)}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Target className="h-5 w-5 text-purple-600" />
                    <span className="font-medium">Search Configuration</span>
                  </div>
                  <p className="text-sm text-gray-600">Set up grounding URLs and search parameters</p>
                </div>
              </div>
            </div>
          </div>

        ) : book?.templateType === 'schoolmate' ? (
          // Schoolmate School Management UI
          <div className="container mx-auto px-6 py-8">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="text-center py-8">
                <div className="mx-auto h-20 w-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-indigo-200">
                  <School className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  School Management System
                </h3>
                <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                  Comprehensive school management with timetables, lesson tracking, attendance monitoring,
                  assessments, and operational planning. Everything you need to run your school effectively.
                </p>

                {/* Feature Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-indigo-50 p-6 rounded-xl border border-indigo-100">
                    <Calendar className="h-8 w-8 text-indigo-600 mx-auto mb-3" />
                    <h4 className="font-semibold text-indigo-900">Timetable & Schedule</h4>
                    <p className="text-sm text-indigo-700 mt-2">Manage class periods, breaks, and teacher assignments</p>
                  </div>
                  <div className="bg-purple-50 p-6 rounded-xl border border-purple-100">
                    <BookOpen className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                    <h4 className="font-semibold text-purple-900">Lessons & Attendance</h4>
                    <p className="text-sm text-purple-700 mt-2">Track lesson delivery, coverage, and student attendance</p>
                  </div>
                  <div className="bg-emerald-50 p-6 rounded-xl border border-emerald-100">
                    <ClipboardList className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
                    <h4 className="font-semibold text-emerald-900">Assessments & Marks</h4>
                    <p className="text-sm text-emerald-700 mt-2">Create tests, capture marks, and generate reports</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowSchoolmateSettings(true)}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 flex items-center gap-2 transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    School Settings
                  </button>
                  <button
                    onClick={() => setShowSchoolmateManager(true)}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 flex items-center gap-2 shadow-lg shadow-indigo-200 transition-colors"
                  >
                    <GraduationCap className="h-4 w-4" />
                    Manage School
                  </button>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="border-t pt-6 mt-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-indigo-50 p-4 rounded-xl">
                    <p className="text-2xl font-bold text-indigo-600">{book.actorCount || 0}</p>
                    <p className="text-sm text-indigo-700">Teachers & Students</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-xl">
                    <p className="text-2xl font-bold text-blue-600">{book.classCount || 0}</p>
                    <p className="text-sm text-blue-700">Classes</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-xl">
                    <p className="text-2xl font-bold text-purple-600">{book.lessonCount || 0}</p>
                    <p className="text-sm text-purple-700">Lessons Recorded</p>
                  </div>
                  <div className="bg-emerald-50 p-4 rounded-xl">
                    <p className="text-2xl font-bold text-emerald-600">{book.assessmentCount || 0}</p>
                    <p className="text-sm text-emerald-700">Assessments</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="border-t pt-6 mt-6">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Quick Actions</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div
                    className="border border-gray-200 rounded-xl p-4 hover:bg-indigo-50 hover:border-indigo-200 cursor-pointer transition-colors group"
                    onClick={() => setShowSchoolmateManager(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Users className="h-5 w-5 text-indigo-600" />
                      <span className="font-medium text-gray-900 group-hover:text-indigo-700">Add Teachers</span>
                    </div>
                    <p className="text-sm text-gray-500">Register staff and assign classes</p>
                  </div>

                  <div
                    className="border border-gray-200 rounded-xl p-4 hover:bg-purple-50 hover:border-purple-200 cursor-pointer transition-colors group"
                    onClick={() => setShowSchoolmateManager(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Calendar className="h-5 w-5 text-purple-600" />
                      <span className="font-medium text-gray-900 group-hover:text-purple-700">Setup Timetable</span>
                    </div>
                    <p className="text-sm text-gray-500">Configure periods and schedules</p>
                  </div>

                  <div
                    className="border border-gray-200 rounded-xl p-4 hover:bg-emerald-50 hover:border-emerald-200 cursor-pointer transition-colors group"
                    onClick={() => window.open(`/books/${bookId}`, '_blank')}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Eye className="h-5 w-5 text-emerald-600" />
                      <span className="font-medium text-gray-900 group-hover:text-emerald-700">Preview System</span>
                    </div>
                    <p className="text-sm text-gray-500">See the teacher/admin view</p>
                  </div>

                  <div
                    className="border border-gray-200 rounded-xl p-4 hover:bg-amber-50 hover:border-amber-200 cursor-pointer transition-colors group"
                    onClick={() => setShowSchoolmateSettings(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Palette className="h-5 w-5 text-amber-600" />
                      <span className="font-medium text-gray-900 group-hover:text-amber-700">Customize Branding</span>
                    </div>
                    <p className="text-sm text-gray-500">School colors, logo, and design</p>
                  </div>
                </div>
              </div>

              {/* Enabled Features */}
              {book?.schoolmateSettings?.enabledFeatures?.length > 0 && (
                <div className="border-t pt-6 mt-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Active Modules</h4>
                  <div className="flex flex-wrap gap-2">
                    {book.schoolmateSettings.enabledFeatures.map(feature => (
                      <span
                        key={feature}
                        className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium capitalize"
                      >
                        {feature.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

        ) : book?.templateType === 'leitner' ? (
          // Leitner editor interface
          <div className="container mx-auto px-6 py-4">
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="text-center py-4">
                <BookOpen className="mx-auto h-16 w-16 text-indigo-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Leitner System Management
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Manage your spaced repetition learning system with custom questions, tests, and progress tracking.
                  Build effective study materials using the scientifically-proven Leitner box method.
                </p>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowLeitnerSettings(true)}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Configure Settings
                  </button>
                  <button
                    onClick={() => setShowLeitnerManager(true)}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <BookOpen className="h-4 w-4" />
                    Manage Questions
                  </button>
                </div>
              </div>

              {/* Learning Tips */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 mt-2">
                <div className="flex items-start gap-4">
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <BookOpen className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2">How the Leitner System Works</h4>
                    <p className="text-sm text-gray-700 mb-3">
                      Questions start in Box 1. When answered correctly, they move to the next box.
                      Incorrect answers send them back to Box 1. Review Box 1 daily, Box 2 every 2 days,
                      Box 3 weekly, Mastered monthly for optimal retention.
                    </p>
                    <div className="grid grid-cols-5 gap-2">
                      {['Box 1', 'Box 2', 'Box 3', 'Mastered'].map((box, idx) => (
                        <div key={box} className="text-center">
                          <div
                            className="h-16 rounded-lg flex items-center justify-center text-white font-semibold text-sm mb-1"
                            style={{
                              backgroundColor: `rgb(${99 + idx * 20}, ${102 + idx * 20}, ${241 - idx * 20})`
                            }}
                          >
                            {box}
                          </div>
                          <p className="text-xs text-gray-600">
                            {idx === 0 ? 'Daily' : idx === 1 ? '2 days' : idx === 2 ? 'Weekly' : idx === 3 ? 'Monthly' : 'Quarterly'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Leitner Stats */}
              <div className="border-t pt-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-indigo-600">{book.questionCount || 0}</p>
                    <p className="text-sm text-indigo-700">Total Questions</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{book.box1Count || 0}</p>
                    <p className="text-sm text-blue-700">Box 1 (New)</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{book.testCount || 0}</p>
                    <p className="text-sm text-purple-700">Total Tests</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {book.published ? 'Active' : 'Draft'}
                    </p>
                    <p className="text-sm text-green-700">Status</p>
                  </div>
                </div>
              </div>

              {/* Feature Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="bg-indigo-50 p-6 rounded-lg">
                  <BookOpen className="h-8 w-8 text-indigo-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-indigo-900 text-center">Leitner System</h4>
                  <p className="text-sm text-indigo-700 mt-2 text-center">
                    5-box spaced repetition system for effective learning
                  </p>
                </div>
                <div className="bg-purple-50 p-6 rounded-lg">
                  <FileText className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-purple-900 text-center">Test Center</h4>
                  <p className="text-sm text-purple-700 mt-2 text-center">
                    Create custom tests and track performance
                  </p>
                </div>
                <div className="bg-blue-50 p-6 rounded-lg">
                  <BarChart3 className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-blue-900 text-center">Analytics</h4>
                  <p className="text-sm text-blue-700 mt-2 text-center">
                    Monitor progress and learning outcomes
                  </p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setShowLeitnerManager(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Plus className="h-5 w-5 text-indigo-600" />
                      <span className="font-medium">Add Questions</span>
                    </div>
                    <p className="text-sm text-gray-600">Create new study questions for the system</p>
                  </div>

                  <div
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setShowLeitnerManager(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="h-5 w-5 text-purple-600" />
                      <span className="font-medium">Create Test</span>
                    </div>
                    <p className="text-sm text-gray-600">Build a custom test from your questions</p>
                  </div>

                  <div
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setShowLeitnerManager(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <BarChart3 className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">View Performance</span>
                    </div>
                    <p className="text-sm text-gray-600">Check analytics and learning progress</p>
                  </div>

                  <div
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => window.open(`/books/${bookId}`, '_blank')}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Eye className="h-5 w-5 text-green-600" />
                      <span className="font-medium">Preview System</span>
                    </div>
                    <p className="text-sm text-gray-600">See how learners will interact with it</p>
                  </div>

                  <div
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setShowLeitnerSettings(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Settings className="h-5 w-5 text-gray-600" />
                      <span className="font-medium">Configure Timer</span>
                    </div>
                    <p className="text-sm text-gray-600">Set time limits and study preferences</p>
                  </div>

                  <div
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setShowLeitnerSettings(true)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Palette className="h-5 w-5 text-orange-600" />
                      <span className="font-medium">Customize Design</span>
                    </div>
                    <p className="text-sm text-gray-600">Brand colors, fonts, and styling</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        ) : book?.templateType === 'institution' ? (
          // Institution editor interface
          <div className="container mx-auto px-6 py-8">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="text-center py-12">
                <School className="mx-auto h-16 w-16 text-indigo-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Institution Management
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Manage your institution's academic programs, staff profiles, events calendar,
                  and resources. Configure admissions processes and showcase your facilities.
                </p>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowInstitutionSettings(true)}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Institution Settings
                  </button>
                  <button
                    onClick={() => setShowInstitutionManager(true)}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <School className="h-4 w-4" />
                    Manage Institution
                  </button>
                </div>
              </div>

              {/* Institution Stats */}
              <div className="border-t pt-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-indigo-600">{book.programCount || 0}</p>
                    <p className="text-sm text-indigo-700">Academic Programs</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{book.staffCount || 0}</p>
                    <p className="text-sm text-blue-700">Staff Members</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{book.eventCount || 0}</p>
                    <p className="text-sm text-green-700">Upcoming Events</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">
                      {book.published ? 'Published' : 'Draft'}
                    </p>
                    <p className="text-sm text-purple-700">Status</p>
                  </div>
                </div>
              </div>

              {/* Institution Features Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="bg-indigo-50 p-6 rounded-lg">
                  <School className="h-8 w-8 text-indigo-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-indigo-900">Academic Programs</h4>
                  <p className="text-sm text-indigo-700 mt-2">Manage courses, departments, and curricula</p>
                </div>
                <div className="bg-blue-50 p-6 rounded-lg">
                  <Users className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-blue-900">Staff & Leadership</h4>
                  <p className="text-sm text-blue-700 mt-2">Organize staff profiles and leadership team</p>
                </div>
                <div className="bg-green-50 p-6 rounded-lg">
                  <Calendar className="h-8 w-8 text-green-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-green-900">Events & Calendar</h4>
                  <p className="text-sm text-green-700 mt-2">Schedule events and academic calendar</p>
                </div>
              </div>
            </div>
          </div>
        ) :
          (
            // Regular editor tabs for other templates
            <div className="max-w-full mx-auto p-6">
              {/* Tab Navigation */}
              <div className="mb-6 border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('content')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'content'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    Content
                  </button>
                  <button
                    onClick={() => setShowBookSettingsModal(true)}
                    className="px-4 py-2 rounded-lg flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <Settings className="h-4 w-4" />
                    Book Settings
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              {activeTab === 'content' ? (
                <TableBookEditor
                  messages={messages}
                  onMessagesChange={handleMessagesChange}
                  book={book}
                />
              ) : (
                <BookSettings
                  book={book}
                  onUpdate={handleBookUpdate}
                />
              )}
            </div>
          )}

        {/* Book Settings Modal */}
        {showBookSettingsModal && (
          <BookSettingsModal
            book={book}
            isOpen={showBookSettingsModal}
            onClose={() => setShowBookSettingsModal(false)}
            onUpdate={handleBookUpdate}
          />
        )}
      </main>

      {/* Catalog Modals */}
      {showCatalogSettings && (
        <CatalogSettingsModal
          catalog={book}
          onSave={async (updatedData) => {
            try {
              await firestoreService.updateBook(bookId, updatedData);
              const updatedBook = await firestoreService.getBook(bookId);
              setBook(updatedBook);
              setShowCatalogSettings(false);
              setHasChanges(false);
            } catch (error) {
              console.error('Error updating catalog settings:', error);
              alert('Failed to update settings');
            }
          }}
          onClose={() => setShowCatalogSettings(false)}
        />
      )}

      {/* Gallery Manager Modal */}
      {showGalleryManager && (
        <GalleryManager
          user={user}
          bookId={bookId}
          bookData={book}
          isOpen={showGalleryManager}
          onClose={() => setShowGalleryManager(false)}
          onUpdate={handleBookUpdate}
        />
      )}

      {/* Album Settings Modal */}
      {showAlbumSettings && (
        <AlbumSettings
          book={book}
          bookId={bookId}
          isOpen={showAlbumSettings}
          onClose={() => setShowAlbumSettings(false)}
          onUpdate={async (updatedBook) => {
            try {
              await firestoreService.updateBook(bookId, updatedBook);
              setBook(updatedBook);
              setHasChanges(false);
            } catch (error) {
              console.error('Error updating book:', error);
              alert('Failed to update book');
            }
          }}
        />
      )}

      {showItemManager && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-end mb-2">
              <button
                onClick={() => setShowItemManager(false)}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Close Manager
              </button>
            </div>

            <ItemManager
              catalogId={bookId}
              catalog={book}
            />
          </div>
        </div>
      )}

      {/* Profile Modals - NEW */}
      {showProfileSettings && (
        <ProfileSettingsModal
          profile={book}
          boook={book}
          onSave={async (updatedData) => {
            try {
              await firestoreService.updateBook(bookId, updatedData);
              const updatedBook = await firestoreService.getBook(bookId);
              setBook(updatedBook);
              setShowProfileSettings(false);
              setHasChanges(false);
            } catch (error) {
              console.error('Error updating profile settings:', error);
              alert('Failed to update settings');
            }
          }}
          onClose={() => setShowProfileSettings(false)}
        />
      )}

      {showProfileManager && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Profile Manager</h2>
              <button
                onClick={() => setShowProfileManager(false)}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Close Manager
              </button>
            </div>

            <ProfileManager
              bookId={bookId}
              book={book}
            />
          </div>
        </div>
      )}

      {showEventManager && (
        <EventManager
          bookId={bookId}
          book={book}
          user={user}
          isOpen={showEventManager}
          onClose={() => setShowEventManager(false)}
          onUpdate={loadBookData}
        />
      )}

      {showEventSettings && (
        <EventSettingsModal
          bookId={bookId}
          book={book}
          isOpen={showEventSettings}
          onClose={() => setShowEventSettings(false)}
          onUpdate={async (updatedBook) => {
            try {
              await firestoreService.updateBook(bookId, updatedBook);
              setBook(updatedBook);
              setHasChanges(false);
            } catch (error) {
              console.error('Error updating book:', error);
              alert('Failed to update book');
            }
          }}
        />
      )}

      {showFlipbookSettings && (
        <FlipbookSettings
          book={book}
          bookId={bookId}
          onUpdate={async (updatedBook) => {
            try {
              await firestoreService.updateBook(bookId, updatedBook);
              setBook(updatedBook);
              setHasChanges(false);
            } catch (error) {
              console.error('Error updating book:', error);
              alert('Failed to update book');
            }
          }}
          onClose={() => setShowFlipbookSettings(false)}
        />
      )}

      {showFlipbookManager && (
        <FlipbookManager
          bookId={bookId}
          bookData={book}
          onUpdate={(updatedBook) => {
            setBook(updatedBook);
            setHasChanges(true);
          }}
          onClose={() => setShowFlipbookManager(false)}
        />
      )}

      {showWebSearchSettings && (
        <WebSearchSettings
          book={book}
          bookId={bookId}
          onUpdate={async (updatedBook) => {
            try {
              await firestoreService.updateBook(bookId, updatedBook);
              setBook(updatedBook);
              setHasChanges(false);
            } catch (error) {
              console.error('Error updating book:', error);
              alert('Failed to update book');
            }
          }}
          onClose={() => setShowWebSearchSettings(false)}
        />
      )}

      {showWebSearchManager && (
        <WebSearchManager
          bookId={bookId}
          book={book}
          onClose={() => setShowWebSearchManager(false)}
        />
      )}

      {/* Leitner Modals */}
      {showLeitnerSettings && (
        <LeitnerSettings
          book={book}
          bookId={bookId}
          isOpen={showLeitnerSettings}
          onClose={() => setShowLeitnerSettings(false)}
          onUpdate={async (updatedBook) => {
            await firestoreService.updateBook(bookId, updatedBook);
            setBook(updatedBook);
            setShowLeitnerSettings(false);
          }}
        />
      )}

      {showLeitnerManager && (
        <LeitnerManager
          bookId={bookId}
          bookData={book}
          isOpen={showLeitnerManager}
          onClose={() => setShowLeitnerManager(false)}
        />
      )}

      {showRequestsSettings && (
        <RequestsSettings
          book={book}
          bookId={bookId}
          isOpen={showRequestsSettings}
          onClose={() => setShowRequestsSettings(false)}
          onUpdate={(updated) => setBook(updated)}
        />
      )}

      {showRequestsManager && (
        <RequestsManager
          book={book}
          bookId={book.id}
          isOpen={showRequestsManager}
          onClose={() => setShowRequestsManager(false)}
        />
      )}

      {showProviderDashboard && (
        <ProviderDashboard
          bookId={book.id}
          bookData={book}
        />
      )}

      {/* Diary Settings Modal */}
      {showDiarySettings && (
        <DiarySettings
          book={book}
          bookId={bookId}
          onUpdate={(updatedBook) => {
            setBook(updatedBook);
            setHasChanges(true);
          }}
          onClose={() => setShowDiarySettings(false)}
        />
      )}

      {/* Diary Manager Modal */}
      {showDiaryManager && (
        <DiaryManager
          bookId={bookId}
          book={book}
          onClose={() => setShowDiaryManager(false)}
        />
      )}


      {/* Organisation Manager Modal */}
      {showOrganisationManager && (
        <OrganisationManager
          book={book}
          user={user}
          bookId={bookId}
          onUpdate={(updatedBook) => {
            setBook(updatedBook);
            setHasChanges(true);
          }}
          onClose={() => setShowOrganisationManager(false)}
        />
      )}

      {/* Organisation Settings Modal */}
      {showOrganisationSettings && (
        <OrganisationSettings
          book={book}
          bookId={bookId}
          onUpdate={(updatedBook) => {
            setBook(updatedBook);
            setHasChanges(true);
          }}
          onClose={() => setShowOrganisationSettings(false)}
        />
      )}

      {/* Schoolmate Settings */}
      {showSchoolmateSettings && (
        <SchoolmateSettings
          book={book}
          bookId={bookId}
          onUpdate={(updated) => setBook(updated)}
          onClose={() => setShowSchoolmateSettings(false)}
        />
      )}

      {/* Schoolmate Manager */}
      {showSchoolmateManager && (
        <SchoolmateManager
          bookId={bookId}
          book={book}
          onClose={() => setShowSchoolmateManager(false)}
        />
      )}



      {/* eCommerce Store Manager Modal */}
      {showEcommerceManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white shadow-2xl w-full max-w-7xl flex flex-col">

            <div className="flex-1 overflow-hidden">
              <EcommerceManager
                bookData={book}
                bookId={bookId}
                onUpdate={handleBookUpdate}
                onClose={() => setShowEcommerceManager(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* eCommerce Settings Modal */}
      {showEcommerceSettings && (
        <EcommerceSettings
          book={book}
          bookId={bookId}
          isOpen={showEcommerceSettings}
          onClose={() => setShowEcommerceSettings(false)}
          onUpdate={async (updatedBook) => {
            try {
              await firestoreService.updateBook(bookId, updatedBook);
              setBook(updatedBook);
              setHasChanges(false);
            } catch (error) {
              console.error('Error updating booking settings:', error);
              alert('Failed to update booking settings');
            }
          }}
        />
      )}

      {showBookingSettings && (
        <BookingSettings
          book={book}
          isOpen={showBookingSettings}
          onClose={() => setShowBookingSettings(false)}
          onUpdate={async (updatedBook) => {
            try {
              await firestoreService.updateBook(bookId, updatedBook);
              setBook(updatedBook);
              setHasChanges(false);
            } catch (error) {
              console.error('Error updating booking settings:', error);
              alert('Failed to update booking settings');
            }
          }}
        />
      )}

      {showBookingManager && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Booking Manager</h2>
              <button
                onClick={() => setShowBookingManager(false)}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Close Manager
              </button>
            </div>

            <BookingManager
              bookId={bookId}
              bookData={book}
              onUpdate={handleBookUpdate}
            />
          </div>
        </div>
      )}

      {showInstitutionManager && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Institution Manager</h2>
              <button
                onClick={() => setShowInstitutionManager(false)}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Close Manager
              </button>
            </div>

            <div className="flex-1 overflow-hidden">
              <InstitutionManager
                bookId={bookId}
                book={book}
                user={user}
              />
            </div>
          </div>
        </div>
      )}

      {showInstitutionSettings && (
        <InstitutionSettings
          book={book}
          bookId={bookId}
          user={user}
          onUpdate={(updatedBook) => {
            setBook(updatedBook);
            setShowInstitutionSettings(false);
          }}
          onClose={() => setShowInstitutionSettings(false)}
        />
      )}

      {showCourseManager && (
        <CourseManager
          bookId={bookId}
          user={user}
          isOpen={showCourseManager}
          onClose={() => setShowCourseManager(false)}
          onUpdate={loadBookData}
        />
      )}

      {showCourseSettings && (
        <CourseSettings
          bookId={bookId}
          bookData={book}
          onUpdate={async (updatedBook) => {
            try {
              await firestoreService.updateBook(bookId, updatedBook);
              setBook(updatedBook);
              setHasChanges(false);
            } catch (error) {
              console.error('Error updating book:', error);
              alert('Failed to update book');
            }
          }}
          onClose={() => setShowCourseSettings(false)}
        />
      )}

      {showSlidesSettings && (
        <SlidesSettings
          book={book}
          bookId={bookId}
          isOpen={showSlidesSettings}
          onClose={() => setShowSlidesSettings(false)}
        />
      )}

      {showSlidesManager && (
        <SlidesManager
          bookId={bookId}
          bookData={book}
          isOpen={showSlidesManager}
          onClose={() => setShowSlidesManager(false)}
        />
      )}
    </div>
  );
};